import { Component, OnInit, ViewChild, Input } from '@angular/core';
import {
  MatTable,
  MatTableDataSource,
} from '@angular/material/table';
import {
  ObservationsKpiData,
  ServiceInsightsTableRow,
} from '../../types/service-insight-types';
import { MatDialog } from '@angular/material/dialog';
import { ImplicationDialogComponent } from '../implication-dialog/implication-dialog.component';
import { LocationsDialogComponent } from '../locations-dialog/locations-dialog.component';
import { FiltersService } from 'src/app/core/services/filters.service';
import { Subscription } from 'rxjs';
import { ServiceInsightsService } from '../../services/service-insights-service.service';
import { ContractModel } from 'src/app/global/contract-filter-model';
import { MixpanelService } from 'src/app/core/services/mixpanel.service';
import { CapitalizePipe } from 'src/app/core/pipes/capitalize.pipe';
import { MatPaginatorIntl } from '@angular/material/paginator';

// TODO: Component leverages Angular Material v16 which has been deprecated.
// Consider updating to the latest version OR moving away from the library to gain more control over the UI and reduce dependency on external libraries.
@Component({
    selector: 'service-insights-table',
    templateUrl: './service-insights-table.component.html',
    styleUrls: ['./service-insights-table.component.scss'],
    providers: [CapitalizePipe, { provide: MatPaginatorIntl }],
    standalone: false
})
export class ServiceInsightsTableComponent implements OnInit {
  @Input() currItems: string[] | undefined;

  @ViewChild(MatTable) table!: MatTable<ServiceInsightsTableRow>;

  filtersSubscription: Subscription | undefined;

  filters: ContractModel | undefined;
  data: ServiceInsightsTableRow[] = [];
  dataSource: MatTableDataSource<ServiceInsightsTableRow> =
    new MatTableDataSource(this.data);
  observationsData: ObservationsKpiData[] = [];

  tabSelected: string = 'overall';
  addAll: boolean = false;
  displayColumns: string[] = [
    'locations',
    'finding',
    'implications',
    'nextSteps',
    'add',
  ];
  tabsAreLoading: boolean = true; // Indicate loading state for the entire section, including the table and other components.
  tabDataIsLoading: boolean = false; // Indicate loading state specifically when user switches tabs on the service insights table.

  dropDownOptions = ['5', '10'];
  currentDropDownOption = '5';

  constructor(
    public dialog: MatDialog,
    private _filterService: FiltersService,
    private _serviceInsightsService: ServiceInsightsService,
    private _mixpanelService: MixpanelService,
    private capitalizePipe: CapitalizePipe,
  ) {}

  async ngOnInit(): Promise<void> {
    this._filterService.selectedFiltersObservable.subscribe(
      (f) => (this.filters = f),
    );

    this.tabsAreLoading = true;
    this.tabDataIsLoading = true;
    if (this.filters !== undefined && this.tabSelected == 'overall') {
      if (this._serviceInsightsService.tableData['overall'].length <= 0)
        this._serviceInsightsService.tableData['overall'] =
          await this.getServiceInsightsData(this.tabSelected);
      this.data = await this.limitData(
        this.currentDropDownOption,
        this._serviceInsightsService.tableData['overall'],
      );

      this.dataSource = new MatTableDataSource(this.data);
      this.observationsData = await this.getObservationsData();
    } else {
      this._serviceInsightsService.tableData['overall'] = [];
      this.data = this._serviceInsightsService.tableData['overall'];
      this.dataSource = new MatTableDataSource(this.data);
      this.observationsData = await this.getObservationsData();
    }
    this.tabsAreLoading = false;
    this.tabDataIsLoading = false;
  }

  /**
   * Changes the currently selected tab in the service insights table and updates the displayed data accordingly.
   * @param tab string representing the tab to switch to (e.g., 'overall', 'issues', 'recurring').
   */
  async changeTab(tab: string) {
    this.tabDataIsLoading = true;

    try {
      this.tabSelected = tab;

      if (this.filters !== undefined) {
        if (
          this._serviceInsightsService.tableData[this.tabSelected].length == 0
        ) {
          this._serviceInsightsService.tableData[this.tabSelected] =
            await this.getServiceInsightsData(this.tabSelected);
        }
        this.data = await this.limitData(
          this.currentDropDownOption,
          this._serviceInsightsService.tableData[this.tabSelected],
        );
        this.dataSource = new MatTableDataSource(this.data);
        this.observationsData = await this.getObservationsData();
      } else {
        this._serviceInsightsService.tableData['overall'] = [];
        this.data = this._serviceInsightsService.tableData['overall'];
        this.dataSource = new MatTableDataSource(this.data);
        this.observationsData = await this.getObservationsData();
      }
      this._mixpanelService.track(
        'Service Insights- ' +
          this.capitalizePipe.transform(tab) +
          ' tab Clicked',
        {
          Tab: tab.toUpperCase(),
          'Bubble Data': this.observationsData,
        },
      );

      this.updateAddAll();
    } catch (error) {
      console.error(
        'An error occurred while changing the service insights table tab:',
        error,
      );
    } finally {
      this.tabDataIsLoading = false;
    }
  }

  async getServiceInsightsData(
    tabSelected: string,
  ): Promise<ServiceInsightsTableRow[]> {
    let arr: ServiceInsightsTableRow[] =
      await this._serviceInsightsService.getServiceInsights(tabSelected);
    let retArr: ServiceInsightsTableRow[] = [];

    arr.forEach((item: ServiceInsightsTableRow) => {
      item.implications = [];
      item.nextSteps = [];
      item.images = '';
      item.checked = false;
      retArr.push(item);
    });

    return retArr;
  }

  async limitData(dropDownOption: string, data: ServiceInsightsTableRow[]) {
    let counter: number = 0;
    let limit = new Number(dropDownOption);
    let retArr: ServiceInsightsTableRow[] = [];
    data.forEach((item: ServiceInsightsTableRow) => {
      if (counter >= limit.valueOf()) {
        return;
      }

      retArr.push(item);
      counter += 1;
    });

    return retArr;
  }

  async getObservationsData() {
    let data = this.dataSource.data.sort(
      (a, b) => b.observationCount - a.observationCount,
    );
    let retArr: ObservationsKpiData[] = [];
    for (let i = 0; i < data.length; ++i) {
      retArr.push({
        observationCount: data[i].observationCount,
        observationName: data[i].observationName,
        sortOrder: i + 1,
      });
    }
    return retArr;
  }

  onClick(record: ServiceInsightsTableRow, nextSteps: boolean) {
    this.dialog.open(ImplicationDialogComponent, {
      data: {
        tab: this.tabSelected,
        findings: record.observationName,
        items: nextSteps ? record.nextSteps : record.implications,
        isNextSteps: nextSteps,
      },
    });
    if (nextSteps) {
      this._mixpanelService.track('Service Insights- Next Step Clicked');
    } else {
      this._mixpanelService.track('Service Insights- Implications Clicked');
    }
  }

  onUnitsClick(observationCount: number, addresses: any[]) {
    this.dialog.open(LocationsDialogComponent, {
      data: {
        units: observationCount,
        addresses: addresses,
      },
    });
    this._mixpanelService.track('Service Insights- Units Clicked', {
      Units: observationCount,
    });
  }

  _addAll(checked: boolean) {
    this.addAll = checked;
    if (this.dataSource.data == null) {
      return;
    }
    this.dataSource.data.forEach((t) => (t.checked = checked));

    this._serviceInsightsService.updateServiceInsightsPresentationData(
      'all',
      this.addAll,
      this.tabSelected,
      this.dataSource.data,
    );
    this._mixpanelService.track(
      'Service Insights- Add All Observations Clicked'
    );
  }

  updateAddAll() {
    this.addAll = this.dataSource.data?.every((t) => t.checked) ?? false;
  }

  _addOne(record: ServiceInsightsTableRow, checked: boolean) {
    this.dataSource.data.map((row) => {
      if (row == record) {
        row.checked = checked;
      }
      return row;
    });

    this.updateAddAll();
    let records = <ServiceInsightsTableRow[]>[record];

    this._serviceInsightsService.updateServiceInsightsPresentationData(
      'single',
      checked,
      this.tabSelected,
      records,
    );
    this._mixpanelService.track(
      'Service Insights- Add Single Observation Clicked',
      {
        Finding: record.observationName,
        Instances: record.observationCount,
        Unit: record.totalLocations,
        Implications: record.implications,
        'Next Steps': record.nextSteps,
      },
    );
  }

  /**
   * Opens the ImplicationDialogComponent for editing the next steps of a service insight.
   * @param record The ServiceInsightsTableRow object representing the service insight to edit.
   * This method is triggered when the user clicks on the pencil icon for next steps in the service insights table.
   */
  onEditNextSteps(record: ServiceInsightsTableRow) {
    this.dialog.open(ImplicationDialogComponent, {
      data: {
        items: record.nextSteps,
        isNextSteps: true,
      },
    });
  }

  /**
   * Opens the ImplicationDialogComponent for editing the implications of a service insight.
   * @param record The ServiceInsightsTableRow object representing the service insight to edit.
   * This method is triggered when the user clicks on the pencil icon for implications in the service insights table.
   */
  onEditImplications(record: ServiceInsightsTableRow) {
    this.dialog.open(ImplicationDialogComponent, {
      data: {
        items: record.implications,
        isNextSteps: false,
      },
    });
  }
}
